// Lädt die Spieldaten aus data/ und gibt ihnen Typen.
// Sim, UI und Harness holen Inhalte nur hierüber, nie direkt aus data/.

import meta from '../../data/meta.json' with { type: 'json' };
import classesJson from '../../data/classes.json' with { type: 'json' };
import speciesJson from '../../data/species.json' with { type: 'json' };
import abilitiesJson from '../../data/abilities.json' with { type: 'json' };
import enemiesJson from '../../data/enemies.json' with { type: 'json' };
import traitsJson from '../../data/traits.json' with { type: 'json' };
import combatJson from '../../data/combat.json' with { type: 'json' };
import lineupsJson from '../../data/lineups.json' with { type: 'json' };
import runJson from '../../data/run.json' with { type: 'json' };
import itemsJson from '../../data/items.json' with { type: 'json' };
import artifactsJson from '../../data/artifacts.json' with { type: 'json' };
import levelsJson from '../../data/levels.json' with { type: 'json' };
import encountersJson from '../../data/encounters.json' with { type: 'json' };
import namesJson from '../../data/names.json' with { type: 'json' };
import lettersJson from '../../data/letters.json' with { type: 'json' };
import reportsJson from '../../data/reports.json' with { type: 'json' };
import bondsJson from '../../data/bonds.json' with { type: 'json' };
import switchesJson from '../../data/switches.json' with { type: 'json' };
import practiceJson from '../../data/practice.json' with { type: 'json' };
import biomesJson from '../../data/biomes.json' with { type: 'json' };

export type GameMeta = {
  title: string;
  titleIsWorkingTitle: boolean;
  subtitle: string;
  placeholderText: string;
};

export const gameMeta: GameMeta = meta;

/** Grundwerte einer Einheit auf dem Ufer (K20). Entfernungen in Feldeinheiten, Zeiten in Sekunden. */
export type UnitStats = {
  hp: number;
  damage: number;
  attackInterval: number;
  /** Abstand von Rand zu Rand, ab dem ein Angriff trifft */
  range: number;
  moveSpeed: number;
  radius: number;
  /** K25: Chance auf Volltreffer */
  critChance: number;
};

// F2, F4: Die Klasse liefert die Stats.
export type FriendClassDef = {
  id: string;
  name: string;
  /** Rolle in einem Satz, für Spieler */
  text?: string;
  concept: string[];
  placeholder?: boolean;
  /** Q62 (Projekt-Lead mit Fabi, 16.10.2026): Die Größe hängt nicht an der Klasse, sondern an der Spezies */
  stats: Omit<UnitStats, 'radius'>;
  /** K41 (Fabi, Phase Kern): ein Merkmal aller Tiere dieser Klasse, mit Schalter (Raufbold: Trotz) */
  trait?: ClassTrait;
};

/** K41: lowHpDamage: so viel mehr Schaden kurz vor dem Ende, linear mit dem fehlenden Leben [ZAHL?] */
export type ClassTrait = { name: string; text: string; lowHpDamage?: number; switch?: SystemId };

// F3, F32: bis zu drei Klassen pro Spezies, je Klasse ein Kniff-Pool.
export type SpeciesClassEntry = {
  friendClass: string;
  kniffe: string[];
  /** S10, X13 (Phase Kern): ausgeblendete frühere Kniffe; sie kommen zurück, wenn ihr Schalter an ist */
  formerKniffe?: string[];
  /** K20/S5: Normaler Angriff heilt statt zu treffen (Waschbär als Beschützer) */
  attackHeal?: number;
  /** R27: Zahlen, die nur in dieser Klasse gelten, Schlüssel „faehigkeit.wert“ */
  params?: Record<string, number>;
  /** R27: Sonderwirkungen dieser Klasse, z. B. splash (Kiesel zerschellen) */
  perks?: Record<string, number>;
  /** K6 (Fabi, Spieltest Phase 4): Mit wie viel Mut der Freund in jeden Kampf startet, Anteil, höchstens ein Drittel */
  startMut?: number;
  /** K6 (Fabi): Anteil am vollen Mut, den der Kniff braucht; 1 = wie alle [ZAHL?] */
  mutNeed?: number;
  /** Fabi (Phase Kern): Was diese Klasse bei dieser Spezies zusätzlich kann, sichtbar auf Karte, beim Aufstellen und im Verlauf */
  trait?: { name: string; text: string };
};

// F1, F28: Passive, Bord- und Begegnungsrolle gehören zur Spezies. Leere Felder sind erlaubt (Q13).
export type SpeciesDef = {
  id: string;
  /** F33: „Martin der Dachs“ */
  article: 'der' | 'die' | 'das';
  name: string;
  concept: string[];
  passive: string;
  classes: SpeciesClassEntry[];
  bonusClass: string | null;
  boardRole: string | null;
  encounterRole: string | null;
  /** F33, Q38 (Fabi): Merkmale für besondere Namen: cute (klein mit Fell oder Federn), small, fur, feathers, cunning,
   *  graceful (grazil gelesen), stocky (eher behäbig gelesen) */
  nameTags?: string[];
  /** K28 (Fabi, Runde 7): wie flink die Spezies auf den Pfoten ist, Faktor auf das Tempo der Klasse [ZAHL?] */
  speed?: number;
  /** Q62: Größe im Kampf in Feldeinheiten; der Kreis folgt der Figur des Grafik-Leads [ZAHL?] */
  radius: number;
  /** S12 (Fabi, 29.10.2026): wie gut die Spezies auf jedem Posten an Bord ist (B25) */
  aboard?: Partial<Record<PostId, Aptitude>>;
};

/** B25 (Fabi, 29.10.2026): die Posten an Bord; wer keinen hat, ruht in der Kajüte */
export type PostId = 'kaeptn' | 'ausguck' | 'gemeinschaft' | 'heiler';
/** S12: gut, normal oder schlecht */
export type Aptitude = 'gut' | 'normal' | 'schlecht';
export const POST_IDS: PostId[] = ['kaeptn', 'ausguck', 'gemeinschaft', 'heiler'];

export type AbilityDef = {
  id: string;
  kind: 'passive' | 'kniff';
  name: string;
  concept: string[];
  placeholder?: boolean;
  /** Spielertext, Deutsch */
  text: string;
  params: Record<string, number>;
  /** Nur Darstellung: flight = Freund fliegt sichtbar zum Ziel und zurück (T9) */
  visual?: 'flight';
  /** Fabi (Phase Kern): kurzes Wort über der Figur, solange der Zustand wirkt (sonst der Name) */
  statusLabel?: string;
  /** Bei Kniffen: welche Spielregel im Kampf dahintersteckt. Neue Kniffe mit bekannter Regel brauchen keinen neuen Code (X3). */
  mechanic?: KniffMechanic;
  /** K46: nur im Übungsufer, keine Spezies hat ihn im Run */
  practice?: boolean;
};

export type KniffMechanic = 'guard' | 'taunt' | 'leap' | 'strike' | 'poison' | 'heal' | 'slow' | 'ram' | 'raid' | 'haste' | 'curl' | 'spin' | 'shield' | 'burrow' | 'buff' | 'dive' | 'rage' | 'volley' | 'blind' | 'rumble' | 'reset' | 'shout' | 'snatch' | 'toxicskin' | 'spray' | 'guardian' | 'pounce' | 'mulde' | 'ball' | 'beckon' | 'weakspot' | 'fell' | 'feint' | 'decoy' | 'skip';

/**
 * G18: nearest = nächster Freund; hindmost = hinterster; raider = springt kurz in die hintere Reihe und wieder zurück.
 * Runde 7 (Fabi): support = schützt die eigenen Schwächsten (G22); flitter = huscht von Freund zu Freund (G23);
 * crowd = zieht langsam zur größten Gruppe Freunde (G24); bounce = wirft sich gegen einen Freund und landet wieder auf
 * Abstand (G25); skirmisher = springt zum Schwächsten in der zweiten Reihe und zurück (G26, bis Phase Kern); hider =
 * versteckt sich hinter einem stärkeren Sturmwesen und wirft von dort, springt nur mit dem Kniff (G26, Phase Kern)
 */
export type EnemyBehavior = 'nearest' | 'hindmost' | 'raider' | 'support' | 'flitter' | 'crowd' | 'bounce' | 'skirmisher' | 'hider';

// G18: Sturmwesen mit Verhalten und Kniff (K22).
export type EnemyDef = {
  id: string;
  name: string;
  /** G30 (Fabi, Phase Kern, 17.10.2026): Spitznamen, wenn mehrere derselben Art im Kampf sind, statt Nummern [WIP] */
  nicknames?: string[];
  /** E31 (Runde 7): für Sätze wie „zwei Schlammlinge und eine Dreckschleuder“ */
  plural?: string;
  article?: 'der' | 'die' | 'das';
  group: 'stormling';
  concept: string[];
  placeholder?: boolean;
  behavior: EnemyBehavior;
  /** G18 Windschnitt: so viele Angriffe oder Sekunden in der hinteren Reihe, dann zurück [ZAHL?] */
  raid?: { attacks: number; seconds: number; /** G26: Umkreis um den vordersten Freund, in dem er sein Opfer sucht */ radius?: number; /** G18: erst nach so vielen Sekunden springen [ZAHL?] */ startDelay?: number };
  /** G23 Blitzflitzer: so viele Schläge bei einem Freund, dann zum nächsten [ZAHL?] */
  flitter?: { min: number; max: number };
  kniff: string;
  /** K33 (Phase Kern): dieser Kniff gilt, solange der zweite Sprung ausgeschaltet ist */
  kniffWithoutJump?: string;
  /** G26 Nieselchen (Fabi, Phase Kern): Reichweite aus dem Versteck, aus der Nähe, und so viel stärker aus der Nähe [ZAHL?] */
  hide?: { range: number; meleeRange: number; meleeBoost: number; /** Fabi (17.10.2026 nachts): Blätter etwas schwächer, dafür der Kniff stärker */ throwFactor?: number };
  /** X13: wie das Wesen vorher war; kommt mit seinem Schalter zurück (G26: nieselchenAlt) */
  previous?: Partial<Omit<EnemyDef, 'stats' | 'previous'>> & { stats?: Partial<UnitStats>; switch: SystemId };
  mutPerAttack?: number;
  /** K6: Anteil am vollen Mut, den der Kniff braucht */
  mutNeed?: number;
  /** G10: Boss am Ende eines Tages */
  boss?: boolean;
  stats: UnitStats;
  /** Vorschlag, über den Fabi noch entscheidet */
  proposal?: boolean;
  /** Idee und Verlauf, für docs/gegner.md (X12) */
  note?: string;
};

// K23: Eigenschaften pro einzelnem Gegner.
export type TraitDef = {
  id: string;
  name: string;
  concept: string[];
  placeholder?: boolean;
  excludes: string[];
  text: string;
  params: Record<string, number>;
  proposal?: boolean;
  /** Idee und Verlauf, für docs/gegner.md (X12) */
  note?: string;
};

/**
 * K15 (Fabi, Phase 3): Rückzug, sobald die Gegner anteilig so viel mehr Leben übrig haben als die Freunde
 * (0,5 heißt: Gegner-Anteil ist das 1,5-Fache des Freunde-Anteils). Ohne Wert: bis zum Letzten.
 */
export type RetreatThreshold = {
  id: string;
  name: string;
  advantageAtLeast?: number;
  default?: boolean;
  /** I26 Hasenfuß (Fabi, Runde 7): Diese Schwelle gibt es nur mit diesem Artefakt */
  needs?: string;
};

export type StormLevel = {
  id: string;
  name: string;
  /** R25 (Fabi, Runde 7): ein Satz, wie es sich auf dem Fluss gerade anfühlt */
  mood?: string;
  enemyBonus: number;
  xpFactor: number;
  /** K21: Böe schiebt die Freunde `duration` Sekunden lang Richtung Gegner, insgesamt etwa `push` Feldeinheiten */
  gust: { every: number; push: number; duration: number } | null;
  lightning: { every: number; count: number; radius: number; damage: number; warning: number } | null;
};

// K20 und alles, was den Kampfrahmen festlegt.
export type CombatRules = {
  concept: string[];
  placeholder: boolean;
  field: {
    width: number;
    height: number;
    friendZone: { xMin: number; xMax: number };
    enemyZone: { xMin: number; xMax: number };
  };
  fieldSlots: { min: number; max: number };
  tickSeconds: number;
  /** F32: Gift tickt so oft */
  poisonTickSeconds: number;
  /** K27: So lange darf ein Marsch zu Kampfbeginn dauern; die Länge ergibt sich aus dem Tempo [ZAHL?] */
  march: { seconds: number; enemyChance: number; enemyAngle: number; /** so nah muss ein Gegner sein, damit man ihn im Vorbeilaufen angreift */ engageGap: number };
  /** K21 (Fabi): Rand, Wind und Stöße [ZAHL?] */
  edge: { grip: number; recoverPerSecond: number; gustTurn: number; treeBlowChance: number; climbSeconds: number; switchMargin: number; switchEvery: number; /** Q62: So groß wie ein Dachs; wer kleiner ist, fliegt bei Stößen weiter [ZAHL?] */ pushRadius: number };
  /** K40 (Fabi, Phase Kern): Freunde blocken sich nicht; nur fast auf demselben Fleck (Anteil der Radien) rücken sie sanft (Anteil je Takt) auseinander [ZAHL?] */
  friendOverlap: { reach: number; push: number };
  maxSeconds: number;
  /** startMax (Fabi, Spieltest Phase 4): Niemand startet mit mehr als einem Drittel Mut */
  mut: { max: number; perAttack: number; perMaxHpTaken: number; startMax: number };
  /** K25: Volltreffer machen so viel mehr Schaden */
  crit: { damageBonus: number };
  /** K21: Ein Blitz, der Sturmwesen trifft, macht sie eine Weile schneller */
  lightningEnemyHaste: { factor: number; duration: number };
  /** K21: Ein Blitz macht getroffene Freunde so viele Sekunden benommen */
  lightningStun: number;
  /** K23: Chance pro Gegner, beim Neuwürfeln eine Eigenschaft zu bekommen */
  traitReroll: { chance: number };
  heavyRain: { startSeconds: number; stepSeconds: number; stepBonus: number };
  /** K54 (Erste Fassung): Kräftemessen. Stehen nur noch wenige auf jeder Seite, werden die Treffer nach und nach härter [ZAHL?] */
  duel?: { maxEach: number; fromSecond: number; afterSeconds: number; stepSeconds: number; stepBonus: number };
  /**
   * K47 (Fabi, 26.10.2026): Geschosse. speed je Flugart in Feldeinheiten pro Sekunde, flight je Bild wie im Manifest,
   * attacks: wer womit wirft (Spezies:Klasse oder Sturmwesen), heal: das Geschoss der Heilung, kniffe: Kniffe mit eigenem
   * Geschoss. overshoot: so weit fliegt ein gerades Geschoss über den Zielort hinaus; coverReach: wer so nah an einem
   * Hindernis steht, schießt darüber hinweg; landReach: so weit um den Landepunkt trifft ein Bogenwurf [ZAHL?]
   */
  projectiles: {
    concept: string[];
    placeholder: boolean;
    speed: Record<string, number>;
    flight: Record<string, string>;
    attacks: Record<string, string>;
    heal: string;
    kniffe: Record<string, string>;
    overshoot: number;
    coverReach: number;
    landReach: number;
  };
  /** K15: Freunde fliehen etwas schneller, als sie sonst laufen, damit sie eine Chance haben [ZAHL?] */
  retreat: { fleeSpeed: number };
  retreatThresholds: RetreatThreshold[];
  xp: {
    perFieldSlot: number;
    evenShare: number;
    killBonus: number;
    /** K23 Feige: Jeder entkommene Gegner nimmt diesen Anteil seines Topf-Stücks mit [ZAHL?] */
    escapePenalty: number;
    weights: { damage: number; taken: number; healed: number; shielded: number };
  };
  stormLevels: StormLevel[];
};

// Phase 3: vorbereitete Gegneraufstellungen mit steigender Schwierigkeit (K7)
export type LineupDef = {
  id: string;
  name: string;
  difficulty: number;
  concept: string[];
  placeholder?: boolean;
  enemies: { enemy: string; traits?: string[]; x: number; y: number }[];
};

export const friendClasses: FriendClassDef[] = classesJson as FriendClassDef[];
export const species: SpeciesDef[] = speciesJson as SpeciesDef[];
export const abilities: AbilityDef[] = abilitiesJson as unknown as AbilityDef[];
export const enemies: EnemyDef[] = enemiesJson as EnemyDef[];
/** F42 (Phase Kern): „vom Treibholzer“, „von der Dreckschleuder“; Befallene und alle anderen beim Namen */
export function fromWhom(name: string): string {
  const e = enemies.find((x) => x.name === name);
  if (!e) return `von ${name}`;
  // „vom Großen Treibholzer“: das Eigenschaftswort vor dem Namen im Dativ
  const words = name.split(' ');
  if (words.length > 1) words[0] = words[0]!.replace(/(er|es|e)$/, 'en');
  return e.article === 'die' ? `von der ${words.join(' ')}` : `vom ${words.join(' ')}`;
}
export const traits: TraitDef[] = traitsJson as TraitDef[];
export const combatRules: CombatRules = combatJson;
export const lineups: LineupDef[] = lineupsJson;

function byId<T extends { id: string }>(list: T[], kind: string): (id: string) => T {
  const map = new Map(list.map((e) => [e.id, e]));
  return (id) => {
    const found = map.get(id);
    if (!found) throw new Error(`Unbekannte ${kind}: ${id}`);
    return found;
  };
}

export const getClass = byId(friendClasses, 'Klasse');
/** K41: das Merkmal der Klasse, wenn sein Schalter an ist */
export const classTrait = (cls: string): ClassTrait | undefined => {
  const t = getClass(cls).trait;
  return t && (!t.switch || systemOn(t.switch)) ? t : undefined;
};
/** Was eine Klasse bei einer Spezies zusätzlich kann: das Merkmal dieser Spezies-Klasse, sonst das der Klasse (K41) */
export const traitOfEntry = (entry: SpeciesClassEntry): { name: string; text: string } | undefined => entry.trait ?? classTrait(entry.friendClass);
export const getSpecies = byId(species, 'Spezies');
/** F2, Q62: Grundwerte eines Freundes, die Klasse liefert die Stats, die Spezies die Größe */
export const friendStats = (speciesId: string, cls: string): UnitStats => ({ ...getClass(cls).stats, radius: getSpecies(speciesId).radius });
export const getAbility = byId(abilities, 'Fähigkeit');

const fmtNumber = (x: number) => (Math.round(x * 10) / 10).toString().replace('.', ',');
/**
 * Zahlen in Texten (Spieltest Phase 4: konkrete Zahlen statt „trifft härter“): {p:faehigkeit.wert} als Zahl,
 * {pct:…} in Prozent, {dmg:…} als Faktor mal Grundschaden. Ohne eigene Werte gelten die Grundwerte der Fähigkeit.
 */
/**
 * Prozent ohne Kommastellen; unter zehn Prozent mit einer, wenn es eine braucht, damit kleine Schritte sichtbar bleiben
 * (Fabi, Phase Kern: „2 % (+0 %)“ bei der Wundheilung des Feuersalamanders)
 */
export function fmtPct(x: number): string {
  const v = x * 100;
  const tenth = Math.round(v * 10) / 10;
  return Math.abs(v) < 10 && Math.abs(tenth - Math.round(tenth)) >= 0.05 ? `${tenth.toFixed(1).replace('.', ',')} %` : `${Math.round(v)} %`;
}

export function fillNumbers(text: string, params: Record<string, number> = {}, damage?: number, before?: { params: Record<string, number>; damage: number }): string {
  const valueIn = (p: Record<string, number>, key: string): number => {
    const [ability, k] = key.split('.') as [string, string];
    return p[key] ?? getAbility(ability).params[k] ?? 0;
  };
  const value = (key: string) => valueIn(params, key);
  // F35 (Fabi, Spieltest Phase 4): Verbesserungen sind immer sichtbar, als „(+…)“ hinter dem neuen Wert;
  // ohne Vorher (etwa im bisherigen Weg) fällt die Klammer weg
  const delta = (kind: string, key: string): string => {
    if (!before) return '';
    if (kind === 'dmg' && damage !== undefined) {
      const d = Math.round(value(key) * damage - valueIn(before.params, key) * before.damage);
      return d > 0 ? ` (+${d} mehr)` : '';
    }
    const d = value(key) - valueIn(before.params, key);
    if (d <= 1e-9) return '';
    return kind === 'pct' ? ` (+${fmtPct(d)})` : ` (+${fmtNumber(d)})`;
  };
  return text
    // Schaden mit Verbesserung in einer Klammer: „245 Schaden (350 %, +35 mehr)“
    .replace(/\{dmg\+:([\w.-]+)\}/g, (_, k: string) => {
      if (damage === undefined) return `${Math.round(value(k) * 100)} % Schaden`;
      const plus = delta('dmg', k).replace(/^ \((.*)\)$/, ', $1');
      return `${Math.round(value(k) * damage)} Schaden (${Math.round(value(k) * 100)} %${plus})`;
    })
    .replace(/\{\+(p|pct|dmg):([\w.-]+)\}/g, (_, kind: string, k: string) => delta(kind, k))
    .replace(/\{p:([\w.-]+)\}/g, (_, k: string) => fmtNumber(value(k)))
    .replace(/\{pct:([\w.-]+)\}/g, (_, k: string) => fmtPct(value(k)))
    // Spieltest Phase 4: Schaden als Zahl und in Prozent vom normalen Angriff
    .replace(/\{dmg:([\w.-]+)\}/g, (_, k: string) => (damage !== undefined ? `${Math.round(value(k) * damage)} Schaden (${Math.round(value(k) * 100)} %)` : `${Math.round(value(k) * 100)} % Schaden`));
}
const enemyById = byId(enemies, 'Gegner');
/** X13: Ein umgebautes Sturmwesen kommt mit seinem Schalter so zurück, wie es vorher war (G26 Nieselchen) */
export const getEnemy = (id: string): EnemyDef => {
  const e = enemyById(id);
  if (!e.previous || !systemOn(e.previous.switch)) return e;
  const { stats, switch: _s, ...rest } = e.previous;
  return { ...e, ...rest, hide: undefined, stats: { ...e.stats, ...stats } };
};
export const getTrait = byId(traits, 'Eigenschaft');

// ---------- Run-Prototyp (Phase 4, R27) ----------

/**
 * Bausteine für Items, Level-Knoten und Artefakte (X3, X6): Ein neuer Inhalt ist eine neue Zeile in den Daten.
 * stat: Grundwert des Freundes; param: Zahl einer Fähigkeit („nussknacker.hits“); kniff: verwandelt den Kniff (F35);
 * perk: Sonderwirkung im Kampf; run: wirkt auf den ganzen Run (Artefakte).
 */
export type StatKey = 'hp' | 'damage' | 'attackSpeed' | 'critChance' | 'range' | 'moveSpeed' | 'mutGain' | 'attackHeal' | 'startMut' | 'mutNeed';
/** fixed: wird bei Items nicht gewürfelt (I24) */
export type Effect =
  | { stat: StatKey; mul?: number; add?: number; fixed?: boolean }
  | { param: string; mul?: number; add?: number; set?: number }
  | { kniff: string }
  | { perk: string; value: number; fixed?: boolean }
  | { run: 'trueSight' | 'regen' | 'itemSlots' | 'gust' | 'lightningRod' | 'retreat'; mul?: number; add?: number; value?: number };

export type StormStageRule = { id: string; above?: number };
/** Q39: Anlegen, Kampf, jeder Kauf, eine Ereignis-Option, jeder Befreiungsversuch, einen Auftrag annehmen */
export type HaltTime = { land: number; fight: number; buy: number; option: number; attempt: number; errand: number };

/** §13, R41 (Erste Fassung): ein Biom je Tag; Gewichte für Sturmwesen, Gefolge am Durchlass, Aussehen [ZAHL?] */
export type BiomeDef = {
  id: string;
  name: string;
  concept: string[];
  placeholder?: boolean;
  /** BI8: Leitfarbe */
  color: string;
  /** Satz, wenn man das Biom erreicht */
  arrive: string;
  /** woraus die Sturmwesen hier sind, kurz (W14) */
  enemies: string;
  /** Faktor auf die Gewichte in run.json (enemyBudget.weights) */
  enemyWeights: Record<string, number>;
  /** Gefolge am Durchlass: Faktor auf die Gewichte */
  boss: { escortWeights: Record<string, number> };
  /** R42: Der Durchlass sieht in jedem Biom anders aus und braucht keine Erklärung */
  gate: { name: string; short: string; color: string };
  /** Platzhalter, bis der Grafik-Lead je Biom einen Boden zeichnet: der Boden des Übungsufers, eingefärbt */
  ground: { tint: string | null; alpha: number };
  leaves: boolean;
};

export const biomes: BiomeDef[] = biomesJson as unknown as BiomeDef[];
const biomeById = new Map(biomes.map((b) => [b.id, b]));
export const getBiome = (id: string): BiomeDef => {
  const b = biomeById.get(id);
  if (!b) throw new Error(`Unbekanntes Biom: ${id}`);
  return b;
};

export type RunRules = {
  concept: string[];
  placeholder: boolean;
  days: number;
  /** R41 (Fabi, 29.10.2026): ein Biom je Tag, in fester Reihenfolge */
  biomeOrder: string[];
  /** E43 (Erste Fassung, Runde 4): so viele Ereignisse aus dem Biom des Tages mindestens [ZAHL?] */
  biomeEventsPerDay: number;
  /** E43: Was die Tiere erzählen und der Run sich merkt (etwa wo man gut überwintert): mögliche Werte, einer gilt je Run */
  lore: Record<string, string[]>;
  /** B25, S12 (Fabi, 29.10.2026): Posten in Schichten und was die Eignung bewirkt [ZAHL?] */
  posts: {
    gemeinschaftPlaces: number[];
    kaeptnXp: Record<Aptitude, number>;
    kaeptnRegen: number;
    heilerRegen: number;
    heilerCare: Record<Aptitude, number>;
    kajuetenRest: number;
    ausguckSight: Record<Aptitude, number>;
    bond: Record<Aptitude, number> & { reach: number };
  };
  /** R19 (Fabi, Spieltest Phase 4): Die Zahl der Anlegestellen schwankt pro Tag, damit Zählen nichts bringt */
  landingsPerDay: { min: number; max: number };
  /** Q39 (Fabi): Ein Halt dauert so lange wie das, was man dort tut, in Stunden Vorsprung [ZAHL?] */
  storm: { startLead: number; driftGain: number; time: HaltTime; stages: StormStageRule[]; clock: StormClock };
  fieldSlots: number[];
  startCrew: number;
  crewSlots: { start: number; perBoss: number };
  /** R27 (Fabi, Spieltest Phase 4): Auf der Weiterfahrt erholen sich alle (travel); wer beim Halt an Bord bleibt,
   *  ruht sich währenddessen zusätzlich ein bisschen aus (restAboard) */
  regen: { travel: number; /** R39: Erholung auf der Fahrt bei fast leerem Leben */ travelHurt?: number; restAboard: number; night: number; treeRegrow: number; /** R32: Rast nach einem gewonnenen Kampf, für den Landtrupp */ afterWin?: number };
  /** F42 (Fabi, Phase Kern): Wer fällt, kommt mit diesem Anteil Leben verwundet zurück [ZAHL?] */
  wound: { hpShare: number };
  /** catchUp (Fabi, Spieltest Phase 4): Aufholen wie bei Pokémon, etwas schwächer: pro Level unter dem stärksten Freund mehr XP, gedeckelt */
  xp: { perDayBonus: number; bossFactor: number; base: number; step: number; choicesUntil: number; maxLevel: number; catchUpPerLevel: number; catchUpMax: number; /** E4 (Fabi): Trost-XP für Verletzte vom Auftrag [ZAHL?] */ errandConsolation: number };
  levelGrowth: { hp: number; damage: number };
  /** R28 (Fabi, Runde 7): Holz pro Sieg und Boss in einem Rahmen; mehr ist seltener [ZAHL?] */
  wood: { start: number; perWin: number[]; perDay: number; bossWin: number[] };
  /** F37, I24 (Fabi, Runde 7): Wie weit Anlagen der Freunde und Werte von Items streuen; ab starAt gibt es einen Stern [ZAHL?] */
  luck: {
    starAt: number;
    itemSpread: number;
    /** I27 (Fabi, Runde 8): so oft ist ein gefundenes Item besonders (★), und um so viel stärker [ZAHL?] */
    itemStarChance: number;
    itemStarRange: number[];
    /** I30 (Fabi, Phase Kern): das Wort vor dem Namen besonderer Items, je Geschlecht */
    specialWords: Record<'m' | 'f' | 'n' | 'pl', string>;
    friend: { hp: number; damage: number; attackSpeed: number; moveSpeed: number; critChance: number; startMut: number };
  };
  visibleAhead: number;
  /** R36 (Fabi, Phase Kern): so viele Frontkämpfer hat die Startcrew mindestens und höchstens */
  startTanks: { classes: string[]; min: number; max: number };
  /** E4: So viele Halte (angelegt, nicht vorbeigetrieben) ist ein Freund auf einem Auftrag unterwegs */
  awayHalts: number;
  /** R27 (Spieltest Phase 4, Vorschlag Fabi): am Durchlass ein Feldplatz mehr für den Boss */
  bossExtraSlot: number;
  /** R27 (Spieltest Phase 4): Ist höchstens noch ein Platz frei, bleibt ein Ereignis mit einem neuen Tier nur mit dieser Chance */
  recruitWhenFullKeep: number;
  /** I22 (Fabi): Gewichte je Seltenheit, wie oft etwas angeboten oder gefunden wird [ZAHL?] */
  rarityWeights: Record<Rarity, number>;
  /** I6 (Fabi): Chance, nach einem gewonnenen Kampf ein Verbrauchsitem zu finden [ZAHL?] */
  consumableDrop: number;
  /** Spieltest Phase 4 (Fabi): ab welcher Anlegestelle eines Tages (je Tag) eine Begegnungsart frühestens kommt [ZAHL?] */
  earliest: Partial<Record<EncounterCategory, number[]>>;
  /** so viele Anlegestellen liegen mindestens zwischen zwei Händlern */
  traderGap: number;
  /** G20 (Fabi): Spielraum für Leben und Schaden jedes Sturmwesens, ± Anteil [ZAHL?] */
  enemyVariance: { hp: number; damage: number };
  /** R27 (Fabi): So viele mehr als Feldplätze soll die Crew haben, damit man rotieren muss [ZAHL?] */
  rotationSpare: number;
  /** R27: Chance, dass ein Ereignis ohne Tier zu einem mit Tier wird, solange die Crew dafür zu klein ist [ZAHL?] */
  recruitWhenShort: number;
  /** I18 (Fabi, Runde 7): Chance je Sturmwesen auf ein Item, und darauf auf ein zweites [ZAHL?] */
  itemCarryChance: number;
  secondItemChance: number;
  traitChance: number;
  /** K23 (Fabi, Runde 7): selten zwei Eigenschaften */
  doubleTraitChance: number;
  encounterWeights: Record<EncounterCategory, number>;
  guaranteedPerDay: EncounterCategory[];
  /** R27: Am ersten Tag wartet unter den ersten so vielen Anlegestellen ein Befallener; 0 = aus */
  earlyRecruitWithin: number;
  /** E28: Welches Ereignis das ist (eine Bewerbung statt eines schweren Kampfs) */
  earlyRecruitEvent: string;
  categories: { id: EncounterCategory; name: string }[];
  /** R6, R7; Fabi (Runde 7): Jedes Zeichen gibt es in drei Fassungen (group), jede mit eigenem Text und eigener Verteilung */
  signs: { id: string; group: string; name: string; text: string; given: Record<EncounterCategory, number> }[];
  /** T36: Knopftexte zum Aufklappen der Möglichkeiten; {tier} ist ein Tier aus dem Landtrupp */
  revealTexts?: string[];
  enemyBudget: {
    perFieldSlot: number;
    perDay: number;
    statPerDay: number;
    /** R14: zusätzliches Budget je Feldplatz und Sturmstufe */
    stormPerSlot: Record<string, number>;
    costs: Record<string, number>;
    weights: Record<string, number>;
    /** Runde 7: manche Sturmwesen kommen erst ab einem bestimmten Tag [ZAHL?] */
    fromDay?: Record<string, number>;
    infestedBase: number;
    infestedPerLevel: number;
    infestedLevel: number[][];
    /** E6 (Fabi, Spieltest Phase 4): Befallene zu retten ist immer schwieriger als ein normaler Kampf (Budget-Faktor) */
    infestedHarder: number;
    /** Q31 (Fabi): Chance, dass Sturmwesen einer Art als Gruppe dicht beieinander stehen (Ziel für Splitterschaden) */
    groupChance: number;
    /** K33 (Fabi, Phase Kern): höchstens so viele Sturmwesen-Arten in einem Kampf */
    maxKinds?: number;
    eventLevel: number[][];
    boss: { enemy: string; escortShare: number; statFactor: number };
    ambushX: number[];
  };
  genericUpgrades: LevelNode[];
};

export type EncounterCategory = 'kampf' | 'befallene' | 'haendler' | 'auftrag' | 'ereignis';

export type ItemDef = {
  id: string;
  name: string;
  kind: 'equip' | 'consumable';
  concept: string[];
  placeholder?: boolean;
  price: number;
  text: string;
  effects?: Effect[];
  /** F22: Was aus dem Item wird, wenn es seine Wirkung verbraucht hat */
  usedAs?: string;
  /** I24: Dieses Item wird nicht gewürfelt (etwa das Versprechen) */
  fixed?: boolean;
  /** I6 (Fabi, Spieltest Phase 4): next wirkt nur im nächsten Kampf */
  use?: { heal?: number; xpBoost?: number; next?: Effect[] };
  notForSale?: boolean;
  /** I22 (Fabi): wie selten es auftaucht; ohne Angabe gewöhnlich */
  /** I30 (Fabi, Phase Kern): Geschlecht für das Wort vor dem Namen besonderer Items (der, die, das, Mehrzahl) */
  gender?: 'm' | 'f' | 'n' | 'pl';
  rarity?: Rarity;
  /** Vorschlag zum Ausprobieren, über den Fabi noch entscheidet */
  proposal?: boolean;
};

/** I22 (Fabi, Spieltest Phase 4): Seltenheit, bestimmt, wie oft etwas auftaucht */
export type Rarity = 'gewoehnlich' | 'selten' | 'sehr-selten';
export type ArtifactDef = {
  id: string;
  name: string;
  concept: string[];
  placeholder?: boolean;
  text: string;
  effects: Effect[];
  rarity?: Rarity;
  proposal?: boolean;
  /** K33 (Phase Kern): wirkt nur mit diesem System und kommt nur, wenn es an ist */
  needs?: SystemId;
};

export type LevelNode = { id: string; name: string; text: string; effects: Effect[] };
export type LevelBranch = { id: string; name: string; text: string; effects: Effect[]; upgrades: LevelNode[][]; /** F35: neue Beschreibung des Kniffs, wenn der Ast ihn verändert */ kniffText?: string; /** Idee und Verlauf, für docs/freunde (X12) */ note?: string };
export type LevelTree = { species: string; friendClass: string; concept: string[]; placeholder?: boolean; branches: LevelBranch[] };

/** E24, E25, E27: Was eine Option eines Ereignisses bewirkt */
/** Fabi (Runde 7): Texte gibt es in mehreren Fassungen, das Spiel wählt eine (in der Doku „ODER“) */
export type Wording = string | string[];

export type EventEffect = {
  recruit?: boolean;
  injure?: 'random' | 'all' | 'actor';
  /** E30 (Fabi, Runde 7): Verletzung vom jetzigen Leben; one = ein zufälliges Tier, spread = zufällig auf den Landtrupp verteilt */
  injureCurrent?: { mode: 'one' | 'spread'; share: number };
  /** R28 (Fabi, Runde 7): ein Wert oder ein Rahmen [von, bis], je Tier gewürfelt */
  share?: number | number[];
  /** Kosten als feste Zahl (negativ), Belohnungen als Rahmen [von, bis] (R28) */
  wood?: number | number[];
  /** ein bestimmtes Item, oder 'random', 'equip', 'consumable', 'prize' (das Streitobjekt); dazu optional eine Seltenheit (I22) */
  item?: string;
  rarity?: Rarity;
  /** I13 (Fabi, Runde 7): ein Artefakt, das man noch nicht hat, nach Seltenheit */
  artifact?: 'random';
  reveal?: number;
  flag?: string;
  unflag?: string;
  /** E27; Runde 7: stärker (Anteil), nur eine Art (only) und jünger (young: Faktor auf die Werte), Merkzeichen bei einem Sieg (onWin) */
  fight?: { subject?: boolean; ambush?: boolean; stronger?: number; only?: string; young?: number; count?: number; onWin?: string };
  consume?: string;
  later?: string;
  within?: number[];
  levelUp?: number;
  farewell?: boolean;
  crewSlot?: number;
  /** E29 (Fabi): heilt den Landtrupp um diesen Anteil vom Leben; als Rahmen je Tier gewürfelt (R28) */
  heal?: number | number[];
  /** Fabi: irgendein Heilmittel aus dem Gepäck verbrauchen (das schwächste) */
  consumeHeal?: boolean;
  /** Fabi (Runde 7): Alle an Bord erholen sich, je Tier gewürfelt (Fröhliche Fete) */
  healCrew?: number | number[];
  /** E31: Die Späher erkunden, welche Sturmwesen am Durchlass warten */
  scoutBoss?: boolean;
  /** E32 (Fabi, Runde 8): XP für alle im Landtrupp (Geschichten des Rothirschs) */
  xpParty?: number | number[];
  /** E32: so viele Stunden Vorsprung vor dem Sturm (eine Abkürzung) */
  lead?: number;
  /** E25 (Runde 7): sich merken, was ihr gerade bekommen habt, unter diesem Namen (etwa für die Amseln) */
  remember?: string;
  /** das Gemerkte zurückgeben, oder seinen Wert in Holz bezahlen */
  returnRemembered?: string;
  payRemembered?: string;
  /** E43 (Erste Fassung, Runde 4): Die Tiere erzählen etwas, das man sich merkt (etwa wo man gut überwintert); der Wert kommt aus run.json (lore) und gilt den ganzen Run */
  tell?: string;
};
export type EventOutcome = { chance: number; text: Wording; effects: EventEffect[] };
export type EventOption = {
  id: string;
  text: string;
  hint?: string;
  /** Q39: wie lange die Option dauert, in Stunden; ohne Angabe die Regel aus run.json (storm.time.option) */
  time?: number;
  /** E24 (Fabi, Spieltest Phase 4): Wer aus dem Landtrupp es macht, wählt man selbst; manche Spezies können es besser.
   *  Dann gilt outcomes[0] bei Erfolg, outcomes[1] bei Misserfolg. */
  actor?: { default: number; chanceBySpecies: Record<string, number>; /** Fabi (Runde 7): wer gar nicht klettern muss, etwa der Eisvogel */ exclude?: string[] };
  requires?: { species?: string; item?: string; wood?: number; /** irgendein Heilmittel im Gepäck */ healItem?: boolean; /** E25: das Gemerkte ist noch im Gepäck */ remembered?: string };
  effects?: EventEffect[];
  outcomes?: EventOutcome[];
  /** E26; Runde 7: tries 0 heißt unbegrenzt, chanceStep erhöht die Chance nach jedem Fehlversuch (Fuchs-Ratespiel); T19: secret verschweigt die Chance */
  /** E33 (Fabi): time = eigene Zeit je Versuch, die auch ohne kleine Zeitkosten (K33) gilt, etwa beim Ratespiel */
  attempt?: { chance: number; tries: number; chanceStep?: number; secret?: boolean; time?: number; success: { text: Wording; effects: EventEffect[] }; fail: { text: Wording; effects: EventEffect[] }; giveUp: { text: Wording; effects: EventEffect[] } };
  result?: Wording;
  /** Fabi (Runde 7): zusätzlicher Satz, wenn diese Spezies im Landtrupp ist */
  resultIf?: { species: string; text: Wording }[];
  /** Fabi (Runde 7): ein Item abgeben, das man selbst wählt; die Seltenheit bestimmt Heilung, Merkzeichen und Text */
  /** Runde 8 (Fabi): Die Heilung nach Seltenheit gilt nur dem Landtrupp (heal) */
  gift?: { kind: 'any' | 'heal'; heal?: Partial<Record<Rarity, number>>; flagByRarity?: string; resultByRarity?: Partial<Record<Rarity, Wording>>; /** E25: merken, was man verschenkt hat */ remember?: string };
  /** E32 (Fabi, Runde 8): Zum Schluss kommt eins davon dazu, nach Gewicht (der Rat des Rothirschs) */
  bonus?: { weight: number; text: Wording; effects: EventEffect[] }[];
  /** Runde 7: Man kann es mehrmals machen, das Ereignis bleibt offen (Glücksrad) */
  repeatable?: boolean;
  /** E36 Elster-Glücksrad (Fabi): Felder mit Breite (weight), was es gibt und was man liest */
  wheel?: { cost: number; segments: { label: string; weight: number; text: Wording; effects: EventEffect[] }[] };
  /** Vorschlag, über den Fabi noch entscheidet */
  proposal?: boolean;
  /** Wartet auf etwas, das es noch nicht gibt (etwa Biber als Freunde); das Spiel zeigt die Option nicht */
  pending?: boolean;
  /** E43 (Erste Fassung): Frage beim Wählen, wer es macht (sonst „Wer soll es machen?“) */
  ask?: string;
  /** E43 Eulen-Legende: Jedes Tier erzählt es anders; Text je Spezies dessen, der es macht (mit actor), sonst result */
  tellBy?: Record<string, Wording>;
  /** E43 Kranichfamilie: Antwort auf eine Frage, deren Lösung der Run sich merkt (lore); outcomes[0] wenn sie stimmt, sonst outcomes[1] */
  quiz?: { key: string; value: string };
};
export type EventDef = {
  id: string;
  kind: 'ereignis' | 'auftrag';
  name: string;
  /** X12: Name in der Doku, wenn der Name im Spiel bei mehreren gleich ist (etwa die drei Botengänge) */
  docLabel?: string;
  concept: string[];
  placeholder?: boolean;
  weight: number;
  text: Wording;
  /** Ein Tier, das man aufnehmen kann */
  subject?: 'animal';
  scheduledOnly?: boolean;
  /** E14: sieht aus wie ein normales Ereignis */
  disguise?: boolean;
  /** Wartet auf Fabis Entscheidung, taucht noch nicht auf */
  pending?: boolean;
  /** E28: Das Tier bringt ein Bewerbungsschreiben mit ({brief}) */
  letter?: boolean;
  /** Runde 7: Das Tier ist immer von dieser Spezies (etwa der Feuersalamander) */
  subjectSpecies?: string;
  /** Fabi (Runde 7): passt zum Zeichen Lärm (Feste, Streit, Hilferufe) */
  noisy?: boolean;
  /** E35 Amselstreit (Runde 7): Worum es geht, wird beim Anlegen gewürfelt ({beute}); effects mit item 'prize' geben genau das */
  prize?: 'random' | 'equip' | 'consumable';
  /** E25 (Runde 7): Folge-Begegnung, die sich an etwas Gemerktes erinnert ({gabe}, {preis}) */
  recalls?: string;
  /** Vorschlag, über den Fabi noch entscheidet */
  proposal?: boolean;
  variants?: { ifFlag: string; text: Wording; options: EventOption[]; /** T36: siehe auto */ auto?: boolean }[];
  options?: EventOption[];
  outcomes?: EventOutcome[];
  /** T36 (Fabi, Phase Kern, 17.10.2026 nachts): Gibt es nichts zu wählen (ein Dank, ein Geschenk), passiert es gleich im ersten Fenster */
  auto?: boolean;
  /** T36: Text des Knopfs, mit dem die Möglichkeiten aufklappen; {tier} ist ein Tier aus dem Landtrupp */
  reveal?: Wording;
  /** R40 (Fabi, Phase Kern, 17.10.2026 nachts): zu welchen Zeichen (Gruppen in run.json) diese Begegnung passt */
  signs?: string[];
  /** E4 (Fabi, Doku 16.10.2026): Was passiert, wenn ihr einen Auftrag ablehnt; sonst nur „Ihr lehnt ab.“ */
  decline?: { text: Wording; effects?: EventEffect[] };
  /** E43 (Erste Fassung, Runde 4): kommt nur in diesen Biomen vor; ohne Angabe überall */
  biomes?: string[];
  /** E43: Was der Run sich gemerkt hat (lore), steht als {ort} in den Texten */
  knows?: string;
};

export const runRules: RunRules = runJson as RunRules;

/** R41 (Fabi, 29.10.2026): Tag 1 Tannenwald, Tag 2 Moor, Tag 3 Weinhänge */
export const biomeOfDay = (day: number): BiomeDef => getBiome(runRules.biomeOrder[Math.max(1, Math.min(day, runRules.biomeOrder.length)) - 1]!);
export const items: ItemDef[] = itemsJson as ItemDef[];
export const artifacts: ArtifactDef[] = artifactsJson as ArtifactDef[];
export const levelTrees: LevelTree[] = levelsJson as LevelTree[];
export const encounters: EventDef[] = encountersJson as EventDef[];
export const friendNames: string[] = namesJson.names;
/** F33, Q38: Namen wie Fussel oder Schalk nur für Spezies mit passenden Merkmalen (nameTags in species.json) */
export const specialNames: { name: string; needs: string[] }[] = namesJson.special;

/** E28: Bausteine für Bewerbungsschreiben */
export type LetterParts = {
  openings: string[];
  closings: string[];
  /** Spieltest Phase 4 (Fabi): Überleitung, mit der der Grund ans Mitreisen anschließt („…, denn …“) */
  reasonBridges: string[];
  byClass: Record<string, string>;
  bySpecies: Record<string, { wish: string[]; reason: string[] }>;
};
export const letterParts: LetterParts = lettersJson as LetterParts;

/** E4 (Spieltest Phase 4): Bericht nach einem Auftrag, in der Stimme der Spezies */
export type ReportTexts = {
  voices: Record<string, { hello: string[]; bye: string[] }>;
  lines: Record<'levelUp' | 'item' | 'wood' | 'woodToo' | 'recruit' | 'injure' | 'nothing', string[]>;
  farewell: { title: string; lines: string[] };
};
export const reportTexts: ReportTexts = reportsJson as unknown as ReportTexts;

/** R33 Bindungspaket (Lead-Auftrag, Fabi, 15.10.2026): Vorstellung, letzte Worte, Reaktionen, Erinnerungen */
export type MemoryKind = 'start' | 'bewerbung' | 'befreit' | 'ereignis' | 'auftrag' | 'title' | 'boss' | 'promise' | 'errand' | 'lastStanding';
export type BondTexts = {
  species: Record<string, { intro: string[]; lastWords: string[] }>;
  reactions: string[];
  memories: Record<MemoryKind, string>;
};
export const bondTexts: BondTexts = bondsJson as unknown as BondTexts;

const itemById = byId(items, 'Item');

// ---------- I24 (Fabi, Runde 7): Items mit gewürfeltem Wert ----------
// Ein Item im Gepäck oder am Freund ist eine Kennung wie „rindenschild~14“: das Item und sein Wurf von 0 bis 20
// (10 ist der Mittelwert). Ohne Wurf gilt der Mittelwert. So bleibt der Spielstand einfaches JSON.
// I27 (Fabi, Runde 8): Besondere Items (★) sind keine Ausreißer der normalen Streuung, sondern eine eigene Klasse:
// „rindenschild~s20“ ist 20 % stärker als die Mitte, besondere liegen zwischen 17 und 23 %.

export function baseItemId(ref: string): string {
  const i = ref.indexOf('~');
  return i < 0 ? ref : ref.slice(0, i);
}
/** Wurf eines Items zwischen −1 und 1; besondere Items zählen hier wie der obere Rand */
export function itemRoll(ref: string): number {
  const i = ref.indexOf('~');
  if (i < 0) return 0;
  if (ref[i + 1] === 's') return 1;
  return (Number(ref.slice(i + 1)) - 10) / 10;
}
/** I27: ein besonderes Item, pct Prozent stärker als die Mitte */
export function makeStarRef(id: string, pct: number): string {
  if (itemById(id).fixed) return id;
  return `${id}~s${Math.round(pct)}`;
}
export function makeItemRef(id: string, roll: number): string {
  if (itemById(id).fixed) return id;
  return `${id}~${Math.max(0, Math.min(20, Math.round((roll + 1) * 10)))}`;
}
export const getItem = (ref: string): ItemDef => itemById(baseItemId(ref));
/** I27: ein besonderes Item */
export function itemStar(ref: string): boolean {
  return ref.includes('~s');
}
/**
 * Name eines Items. I30 (Fabi, Phase Kern): Besondere Items tragen ein Wort vor dem Namen („Prächtiger Federkiel“)
 * statt eines Sterns; ein Name aus mehreren Wörtern rückt dahinter klein („Prächtiger spitzer Dorn“).
 */
export function itemLabel(ref: string): string {
  const def = getItem(ref);
  if (!itemStar(ref)) return def.name;
  if (systemOn('itemSpread')) return `${def.name} ★`;
  const [first, ...rest] = def.name.split(' ');
  const name = rest.length ? [first!.charAt(0).toLowerCase() + first!.slice(1), ...rest].join(' ') : def.name;
  return `${runRules.luck.specialWords[def.gender ?? 'n']} ${name}`;
}

/** Ein Baustein um den Faktor k stärker oder schwächer (k = 1 ist der Mittelwert) */
function scaleEffect(e: Effect, k: number): Effect {
  if ('fixed' in e && e.fixed) return e;
  if ('stat' in e) return { ...e, ...(e.mul !== undefined ? { mul: 1 + (e.mul - 1) * k } : {}), ...(e.add !== undefined ? { add: e.add * k } : {}) };
  if ('perk' in e && e.perk !== 'revive') return { ...e, value: e.value * k };
  return e;
}
const itemFactor = (ref: string): number => {
  if (getItem(ref).fixed) return 1;
  const star = /~s(\d+)$/.exec(ref);
  return star ? 1 + Number(star[1]) / 100 : 1 + itemRoll(ref) * runRules.luck.itemSpread;
};
/** Was ein angelegtes Item mit seinem Wurf bewirkt */
export function itemEffects(ref: string): Effect[] {
  const k = itemFactor(ref);
  return (getItem(ref).effects ?? []).map((e) => scaleEffect(e, k));
}
/** Was ein Verbrauchsitem mit seinem Wurf bewirkt */
export function itemUse(ref: string): NonNullable<ItemDef['use']> {
  const use = getItem(ref).use ?? {};
  const k = itemFactor(ref);
  return {
    ...(use.heal !== undefined ? { heal: use.heal * k } : {}),
    ...(use.xpBoost !== undefined ? { xpBoost: use.xpBoost * k } : {}),
    ...(use.next ? { next: use.next.map((e) => scaleEffect(e, k)) } : {}),
  };
}

/** Wert eines Bausteins als Text, wie er in Itemtexten steht: „15 %“, „12 Prozentpunkte“, „6“ */
function effectValue(e: Effect): string {
  if ('stat' in e) {
    if (e.stat === 'critChance') return `${Math.round((e.add ?? 0) * 100)} Prozentpunkte`;
    if (e.stat === 'startMut') return `${Math.round((e.add ?? 0) * 100)}`;
    if (e.mul !== undefined) return fmtPct(e.mul - 1);
    return fmtNumber(e.add ?? 0);
  }
  if ('perk' in e) {
    if (e.perk === 'xpPerKniff' || e.perk === 'nutHeal' || e.perk === 'critEvery') return `${Math.round(e.value)}`;
    // I25 Staubpilz: Sekunden
    if (e.perk === 'sporeCloud' || e.perk === 'amberGuard') return `${fmtNumber(Math.round(e.value * 10) / 10)} Sekunden`;
    return fmtPct(e.value);
  }
  return '';
}
function valuesOf(def: ItemDef, effects: Effect[], use: NonNullable<ItemDef['use']>): string[] {
  if (def.use) return [...(use.heal !== undefined ? [fmtPct(use.heal)] : []), ...(use.xpBoost !== undefined ? [fmtPct(use.xpBoost)] : []), ...(use.next ?? []).map(effectValue)];
  return effects.map(effectValue);
}
/** Itemtext mit den Zahlen dieses Items: {w} ist der erste Wert, {w2} der zweite … */
export function itemText(ref: string): string {
  const def = getItem(ref);
  const vals = valuesOf(def, itemEffects(ref), itemUse(ref));
  return def.text.replace(/\{w(\d?)\}/g, (_, n: string) => vals[(n ? Number(n) : 1) - 1] ?? '');
}
/** Itemtext mit dem Rahmen, in dem die Zahlen liegen können (für die Doku) */
export function itemTextRange(id: string): string {
  const def = getItem(id);
  if (def.fixed) return itemText(id);
  // I30 (Phase Kern): Ohne Streuung hat jedes Item die festen Zahlen der Mitte
  if (!systemOn('itemSpread')) return itemText(makeItemRef(id, 0));
  const lo = valuesOf(def, itemEffects(`${id}~0`), itemUse(`${id}~0`));
  const hi = valuesOf(def, itemEffects(`${id}~20`), itemUse(`${id}~20`));
  return def.text.replace(/\{w(\d?)\}/g, (_, n: string) => {
    const i = (n ? Number(n) : 1) - 1;
    const a = lo[i] ?? '';
    const z = hi[i] ?? '';
    if (a === z) return a;
    // „12 %“ bis „18 %“ wird „12–18 %“
    const unit = /\s\S+$/.exec(a)?.[0] ?? '';
    return unit && z.endsWith(unit) ? `${a.slice(0, -unit.length)}–${z}` : `${a}–${z}`;
  });
}
export const getArtifact = byId(artifacts, 'Artefakt');
export const getEncounter = byId(encounters, 'Begegnung');
export function getLevelTree(speciesId: string, friendClass: string): LevelTree | undefined {
  return levelTrees.find((t) => t.species === speciesId && t.friendClass === friendClass);
}

// ---------- Ausgeblendet (Phase Kern) ----------

/** K33, I30, X13 (Fabi, Phase Kern): Systeme werden über Schalter ausgeblendet, nicht gelöscht (data/switches.json) */
/**
 * R38 (Fabi, Phase Kern): das Unwetter als Uhr. startLead: Vorsprung am Morgen, ohne Zugewinn beim Vorbeitreiben;
 * weakenPerWin: so viel Kraft verliert der Durchlass mit jedem gewonnenen Kampf unterwegs, bis floor; retryRest und
 * retryTime: Rast nach einer Niederlage am Durchlass (Erholung und Zeit) [ZAHL?]
 */
export type StormClock = { startLead: number; weakenPerWin: number; floor: number; retryRest: number; retryTime: number };

export type SystemId = 'wind' | 'edgeOut' | 'march' | 'secondJump' | 'lightning' | 'lightningDamage' | 'smallTimeCosts' | 'itemSpread' | 'firstFallFarewell' | 'retreatChoice' | 'recruitWhenShort' | 'weakTargetSwitch' | 'igelburg' | 'nieselchenAlt' | 'raufboldTrotz' | 'retreat' | 'stormDrift' | 'uebungsufer';
/** kniff: ein früherer Kniff, der wiederkommt, wenn der Schalter an ist (S10, Igelburg) */
export type SystemSwitch = { on: boolean; name: string; what: string; why: string; review: string; reviewWhy: string; open?: boolean; kniff?: string };
export type HiddenList = { off: string[]; why: string; review: string; reviewWhy: string };
/** Fabi (Phase Kern): Was nicht aus, sondern nur weniger ist, behalten wir genauso im Blick */
export type ReducedNote = { name: string; what: string; before: string; why: string; review: string; reviewWhy: string };
export type Switches = { concept: string[]; note: string; systems: Record<SystemId, SystemSwitch>; traits: HiddenList; items: HiddenList; reduced: ReducedNote[] };
export const switches: Switches = switchesJson as Switches;

/** K46 Übungsufer: ein fester Kampf zum Ausprobieren von Pause, Befehlen und Kniffen */
export type PracticeFriend = { species: string; friendClass: string; kniff: string; passive?: string; name: string; x: number; y: number; /** so schnell füllt sich der Mut, Faktor [ZAHL?] */ mutGain: number; startMut: number };
export type PracticeDef = {
  concept: string[];
  placeholder: boolean;
  note: string;
  name: string;
  seed: number;
  stormLevel: string;
  hpFactor: number;
  friends: PracticeFriend[];
  enemies: { enemy: string; x: number; y: number; mutGain?: number; kniff?: string }[];
  /** art: das Bild im Manifest (marker; beim Gebüsch dazu <art>-vorn) */
  obstacles: { id: string; kind: 'stone' | 'log' | 'bush'; name: string; art?: string; x: number; y: number; radius: number; x2?: number; y2?: number }[];
};
export const practice: PracticeDef = practiceJson as PracticeDef;

export const systemOn = (id: SystemId): boolean => switches.systems[id].on;
export const itemHidden = (ref: string): boolean => switches.items.off.includes(baseItemId(ref));
export const traitHidden = (id: string): boolean => switches.traits.off.includes(id);
/** Was Händler, Funde, Ereignisse und Sturmwesen anbieten: nichts Unverkäufliches, nichts Ausgeblendetes */
export const itemOffered = (i: ItemDef): boolean => !i.notForSale && !itemHidden(i.id);
/** K23: Eigenschaften, die gewürfelt werden */
export const activeTraits = (): TraitDef[] => traits.filter((t) => !traitHidden(t.id));
/** Artefakte, die zu einem ausgeschalteten System gehören (Wurzelwerk und Wind), kommen nicht */
export const artifactOffered = (a: ArtifactDef): boolean => !a.needs || systemOn(a.needs);
/** K33: Kniff eines Sturmwesens, je nachdem, ob der zweite Sprung an ist */
export const enemyKniff = (e: EnemyDef): string => (!systemOn('secondJump') && e.kniffWithoutJump ? e.kniffWithoutJump : e.kniff);
/**
 * S10, X13 (Phase Kern): die Kniffe einer Klasse. Ein früherer Kniff (formerKniffe) ist ausgeblendet; ist sein Schalter
 * an, steht er wieder vorn und ist der Kniff, mit dem der Freund kämpft.
 */
export function classKniffe(entry: Pick<SpeciesClassEntry, 'kniffe' | 'formerKniffe'>): string[] {
  const back = (entry.formerKniffe ?? []).filter((k) => Object.values(switches.systems).some((sw) => sw.kniff === k && sw.on));
  return [...back, ...entry.kniffe];
}
export const defaultKniff = (entry: Pick<SpeciesClassEntry, 'kniffe' | 'formerKniffe'>): string | null => classKniffe(entry)[0] ?? null;
/** Q39, K33: Zeit fürs Kaufen und für Versuche, solange die kleinen Zeitkosten an sind */
export const smallTime = (kind: 'buy' | 'attempt'): number => (systemOn('smallTimeCosts') ? runRules.storm.time[kind] : 0);
/** E33 (Fabi): Beim Ratespiel kostet jeder Tipp Zeit, das ist der Kern des Ereignisses; sonst gelten die kleinen Zeitkosten */
export const attemptTime = (o: EventOption): number => o.attempt?.time ?? smallTime('attempt');

// ---------- Herkunft im Kampf (Phase Kern) ----------

/** Fabi (Phase Kern): Woher etwas im Kampf kommt, in Worten; via wie in den Kampfereignissen, etwa 'item:holunderkette' */
export type ViaInfo = { kind: string; id: string; name: string; gender?: 'm' | 'f' | 'n' | 'pl'; short: string };

function findLevelNode(id: string): { name: string } | undefined {
  for (const t of levelTrees)
    for (const br of t.branches) {
      if (br.id === id) return br;
      for (const lvl of br.upgrades) for (const n of lvl) if (n.id === id) return n;
    }
  return undefined;
}

export function viaInfo(via: string): ViaInfo {
  const [kind, ...rest] = via.split(':');
  const id = rest.join(':');
  const plain = (name: string) => ({ kind: kind!, id, name, short: name });
  switch (kind) {
    case 'item': {
      const def = itemById(id);
      return { ...plain(def.name), gender: def.gender ?? 'n' };
    }
    case 'artifact':
      return plain(getArtifact(id).name);
    case 'kniff':
    case 'passive': {
      const a = getAbility(id);
      return { ...plain(a.name), short: a.statusLabel ?? a.name };
    }
    case 'branch':
    case 'node':
      return plain(findLevelNode(id)?.name ?? id);
    case 'class': {
      const [sp, cls] = id.split(':') as [string, string];
      const entry = getSpecies(sp).classes.find((c) => c.friendClass === cls);
      return plain((entry ? traitOfEntry(entry)?.name : undefined) ?? getClass(cls).name);
    }
    case 'trait':
      return plain(getTrait(id).name);
    case 'storm':
      return plain(id === 'lightning' ? 'Blitz' : id === 'wind' ? 'Wind' : id);
    case 'behavior':
      return plain(id === 'support' ? 'Dreckschild' : id);
    default:
      return plain(via);
  }
}

/** „mit der Holunderkette“, „durch Heißhunger“: das passende Wort davor, Items im Dativ mit Artikel */
export function viaPhrase(via: string): string {
  return via
    .split('|')
    .map((one) => {
      const v = viaInfo(one);
      if (v.kind !== 'item') return `durch ${v.name}`;
      const article = { m: 'dem', f: 'der', n: 'dem', pl: 'den' }[v.gender ?? 'n'];
      const words = v.name.split(' ');
      // Eigenschaftswörter vor dem Namen im Dativ: „Verstärktes Rindenschild“ wird „dem verstärkten Rindenschild“
      const dative = words.map((w, i) => {
        if (i === words.length - 1) return w;
        const low = w.charAt(0).toLowerCase() + w.slice(1);
        if (/(er|es)$/.test(low)) return `${low.slice(0, -2)}en`;
        if (/e$/.test(low)) return `${low}n`;
        return low;
      });
      return `mit ${article} ${dative.join(' ')}`;
    })
    .join(' und ');
}

/** Kurzer Name einer Herkunft, etwa für Klammern im Verlauf und Wörter über der Figur */
export const viaName = (via: string): string => via.split('|').map((v) => viaInfo(v).name).join(', ');
